// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { PodStatus } from '@/components/pods/PodStatus';
import {
	getPodModeCapability,
	getPodPlatformLabel,
	Pod,
	PodMember,
} from '@/types/pods';
import { Id } from '@/convex/_generated/dataModel';
import { truncateKey } from '@/utils/truncateKey';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileGroup } from '@/types/profileGroups';
import { cn } from '@/lib/utils';
import { useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useMutation } from 'convex/react';
import { useToast } from '@/components/ui/use-toast';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog';
import { useQuery } from 'convex/react';
import { ProgressDialog } from '@/components/ui/progress-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { AddUrlDialog } from '../AddUrlDialog';
import { useSelectedPodStore } from '@/stores/selectedPodStore';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import ReactMarkdown from 'react-markdown';
import { URLsSection } from '../URLsSection';
import { ProfileCard } from '../ProfileCard';
import { AddProfileSection } from '../AddProfileSection';
import { formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Badge } from '@/components/ui/badge';
import { ScheduleStatusBadge } from '@/components/admin/ScheduleStatusBadge';

interface BrandVoiceOption {
	id: string;
	name: string;
	description: string;
	emoji: string;
	style: string;
}

const BRAND_VOICES: BrandVoiceOption[] = [
	{
		id: 'prometheus',
		name: 'Prometheus',
		description: 'Social media influencer style',
		emoji: '🔥',
		style: 'Engaging and influential social media personality',
	},
	{
		id: 'reverend',
		name: 'The Reverend',
		description: 'Aggressive, story-driven, informal, and punchy',
		emoji: '🎭',
		style: 'Bold and dramatic storyteller',
	},
	{
		id: 'brent',
		name: 'Brent Ecomm Master',
		description: 'Professional, conversational, and accessible',
		emoji: '💼',
		style: 'Approachable business expert',
	},
];

// Define the common styles outside the component
const brandVoiceStyles = {
	base: 'relative flex items-start space-x-4 p-6 rounded-lg transition-all duration-200 bg-white dark:bg-gray-900 cursor-pointer',
	selected:
		'border-2 border-pink-500 shadow-[0_0_35px_rgba(236,72,153,0.6)] ring-2 ring-pink-500 scale-[1.02]',
	unselected:
		'border border-gray-50/10 hover:border-2 hover:border-purple-500 hover:shadow-[0_0_15px_rgba(147,51,234,0.3)] hover:scale-[1.02]',
	radio: 'peer absolute left-4 top-[50%] translate-y-[-50%] h-5 w-5 border-2 border-pink-300 data-[state=checked]:border-pink-500 data-[state=checked]:bg-pink-500 data-[state=checked]:text-white',
	radioIcon:
		'absolute left-[1.5px] top-[1.5px] h-4 w-4 scale-0 text-current transition-transform duration-200 data-[state=checked]:scale-100',
	content: 'pl-8 flex-1',
};

interface PodExpandedViewProps {
	pod: Pod;
	onClose: () => void;
	handleToggleActive: (podId: Id<'engagementPods'>) => Promise<void>;
	handleDeletePod: (podId: Id<'engagementPods'>) => Promise<void>;
	handleRenamePod: (podId: Id<'engagementPods'>, newName: string) => void;
	handleAddMember: (
		podId: Id<'engagementPods'>,
		member: PodMember,
		triggerConfettiEffect?: boolean
	) => Promise<void>;
	handleToggleProfileActive: (profile: any, pod: Pod) => Promise<void>;
	handleRemoveMember: (
		podId: Id<'engagementPods'>,
		email: string
	) => Promise<void>;
	podButtonDisabled: Record<string, boolean>;
	profileButtonDisabled: Record<string, boolean>;
	runningPods: Record<string, boolean>;
	newMember: PodMember;
	setNewMember: (member: PodMember) => void;
	typedProfiles: any[] | undefined;
	setPodToStart: (
		data: { podId: Id<'engagementPods'>; urls: any[] } | null
	) => void;
	fetchPodUrls: (podId: Id<'engagementPods'>) => any[];
	profileGroups: ProfileGroup[];
	handleAddProfileGroup: (
		podId: Id<'engagementPods'>,
		groupId: Id<'profileGroups'>
	) => Promise<void>;
	handleStartPod: (podId: Id<'engagementPods'>) => Promise<void>;
	urls: any[];
	handleToggleUrlActive: (urlId: Id<'urls'>) => Promise<void>;
	handleArchiveUrl: (urlId: Id<'urls'>) => Promise<void>;
	urlButtonDisabled: Record<string, boolean>;
	triggerConfetti: () => void;
	handleBulkRemoveMembers: (
		podId: Id<'engagementPods'>,
		emails: string[],
		onProgress?: (current: number, email: string) => void
	) => Promise<void>;
	setRunningPods: React.Dispatch<
		React.SetStateAction<Record<string, boolean>>
	>;
	documents?: any[];
}

export const PodExpandedView: React.FC<PodExpandedViewProps> = ({
	pod,
	onClose,
	handleToggleActive,
	handleDeletePod,
	handleRenamePod,
	handleAddMember,
	handleToggleProfileActive,
	handleRemoveMember,
	podButtonDisabled,
	profileButtonDisabled,
	runningPods,
	newMember,
	setNewMember,
	typedProfiles,
	setPodToStart,
	fetchPodUrls,
	profileGroups,
	handleAddProfileGroup,
	handleStartPod,
	urls,
	handleToggleUrlActive,
	handleArchiveUrl,
	urlButtonDisabled,
	triggerConfetti,
	handleBulkRemoveMembers,
	setRunningPods,
	documents,
}) => {
	const [podToDelete, setPodToDelete] = useState<Id<'engagementPods'> | null>(
		null
	);
	const [selectedGroupId, setSelectedGroupId] =
		useState<Id<'profileGroups'> | null>(null);
	const [podToRename, setPodToRename] = useState<{
		id: Id<'engagementPods'>;
		name: string;
	} | null>(null);
	const [expandedGroups, setExpandedGroups] = useState<
		Record<string, boolean>
	>({
		main: true,
		urls: true,
	});
	const [removeProgress, setRemoveProgress] = useState<{
		current: number;
		total: number;
		currentProfile: string;
	} | null>(null);

	// Debug logging
	React.useEffect(() => {
		console.log('PodExpandedView Debug:', {
			typedProfiles: typedProfiles?.length || 0,
			profileGroups: profileGroups?.length || 0,
			pod: pod?.name,
			podMode: pod?.mode
		});
	}, [typedProfiles, profileGroups, pod]);
	const [addProgress, setAddProgress] = useState<{
		current: number;
		total: number;
		currentProfile: string;
	} | null>(null);
	const [memberToRemove, setMemberToRemove] = useState<{
		email: string;
		podId: Id<'engagementPods'>;
	} | null>(null);
	const [groupToRemove, setGroupToRemove] = useState<{
		groupId: Id<'profileGroups'>;
		podId: Id<'engagementPods'>;
		emails: string[];
	} | null>(null);
	const [urlToArchive, setUrlToArchive] = React.useState<Id<'urls'> | null>(
		null
	);
	const [showAddUrlDialog, setShowAddUrlDialog] = React.useState(false);
	const [showResetConfirm, setShowResetConfirm] = useState(false);
	const [searchTerm, setSearchTerm] = React.useState('');
	const [url, setUrl] = React.useState('');
	const [selectedBrandVoice, setSelectedBrandVoice] = useState<string>(
		pod.brandVoice?.id || 'prometheus'
	);
	const [userBrandVoice, setUserBrandVoice] = useState<any>(null);
	const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
	const [currentAnalysis, setCurrentAnalysis] = useState<string | null>(null);
	const [isAddingMember, setIsAddingMember] = useState(false);
	const [isUpdatingBrandVoice, setIsUpdatingBrandVoice] = useState(false);
	const [brandVoiceToConfirm, setBrandVoiceToConfirm] = useState<{
		value: string;
		name: string;
		description: string;
		emoji: string;
	} | null>(null);
	const [activeTab, setActiveTab] = useState('overview');
	const [viewingBrandVoice, setViewingBrandVoice] = useState<{
		id: string;
		name: string;
		emoji: string;
	} | null>(null);

	const { toast } = useToast();
	const updateStatus = useMutation(api.engagementPods.updateStatus);
	const schedulePod = useMutation(api.engagementPods.schedulePod);
	const updatePodName = useMutation(api.urls.updatePodName);
	const updateBrandVoice = useMutation(api.engagementPods.updateBrandVoice);

	const { setSelectedPod } = useSelectedPodStore();

	const navigate = useNavigate();

	const podSchedules = useQuery(api.schedules.getByTargetId, {
		targetId: pod._id,
	});

	const hasActiveSchedule = podSchedules?.some(
		(schedule) =>
			schedule.status === 'active' &&
			new Date(schedule.lastRun ?? new Date().toISOString()) > new Date()
	);

	const isPodRunning = (status?: string) => {
		const runningStatuses = [
			'POD STARTING',
			'INITIALIZING',
			'🔨 SCRAPING POST',
			'🔌 SCRAPING DONE',
			'✍️ Commenting...',
			'✍️ Replying...',
			'✅ COMMENTED',
			'👀 Logging...',
			'😴 Sleeping...',
		];
		return status ? runningStatuses.includes(status.trim()) : false;
	};

	const { user, isAdmin } = useAuth();

	// Get the current user's profile
	const userProfile = useQuery(api.profiles.getProfileByEmail, {
		email: user?.email || 'skip',
	});

	const brentDocs = useQuery(api.documents.getByEmail, {
		email: 'brent@tortugaseo.com',
	});
	const reverendDocs = useQuery(api.documents.getByEmail, {
		email: 'matt@thetrainermethod.com',
	});

	// Add these queries
	const brentProfile = useQuery(api.profiles.getProfileByEmail, {
		email: 'brent@tortugaseo.com',
	});
	const reverendProfile = useQuery(api.profiles.getProfileByEmail, {
		email: 'matt@thetrainermethod.com',
	});

	useEffect(() => {
		const isPodCurrentlyRunning = isPodRunning(pod.lastStatus?.status);

		// Update running status whenever pod status changes or component mounts
		setRunningPods((prev) => ({
			...prev,
			[pod._id]: isPodCurrentlyRunning,
		}));

		// If pod is running, set up an interval to check status
		if (isPodCurrentlyRunning) {
			const interval = setInterval(() => {
				if (!isPodRunning(pod.lastStatus?.status)) {
					setRunningPods((prev) => ({
						...prev,
						[pod._id]: false,
					}));
					clearInterval(interval);
				}
			}, 1000);

			// Cleanup interval on unmount or when status changes
			return () => clearInterval(interval);
		}
	}, [pod.lastStatus?.status, pod._id, setRunningPods]);

	// First, add a query to get profile by profileKey
	const getProfileByKey = useQuery(
		api.profiles.getByProfileKey,
		newMember.uploadPostProfileKey
			? { profileKey: newMember.uploadPostProfileKey }
			: 'skip'
	);

	const checkUrlExists = useQuery(api.urls.checkUrlExists, {
		url: url || '',
		podId: pod.mode === 'swarm' ? pod._id : null,
		mode: pod.mode || 'swarm',
	});

	// Update the URLs query to include both pod and profile URLs
	const podUrls = useQuery(api.urls.list, {
		podId: pod._id,
		profileKey:
			pod.mode === 'broadcast'
				? pod.members[0]?.uploadPostProfileKey || null
				: null,
	});

	// Add debug logging
	console.log('Pod URLs Query:', {
		podId: pod._id,
		profileKey:
			pod.mode === 'broadcast' ? pod.members[0]?.uploadPostProfileKey : null,
		results: podUrls,
	});

	useEffect(() => {
		// Check if user has a profile with writing analysis or documents
		if (userProfile?.writingAnalysis || (documents && documents.length > 0)) {
			setUserBrandVoice({
				id: 'user',
				name: 'My Brand Voice',
				description: 'Your personalized brand voice',
				emoji: '✨',
				style: userProfile?.writingAnalysis ? 'Custom brand voice based on your content' : 'Custom brand voice based on your documents',
			});
			if (!pod?.brandVoice?.id) {
				setSelectedBrandVoice('user');
			}
		}
	}, [documents, userProfile, pod?.brandVoice?.id]);

	// Add this query to get the profile's writing analysis
	const selectedProfileAnalysis = useQuery(
		api.profiles.getWritingAnalysis,
		pod.brandVoice?.profileId
			? { profileId: pod.brandVoice.profileId }
			: 'skip'
	);

	// Add these conditions at the top of the component
	const hasProfile = pod.members && pod.members.length > 0;
	const hasActiveUrls = urls?.some((url) => url.isActive);
	const podMode = pod.mode === 'broadcast' ? 'broadcast' : 'swarm';
	const modeCapability = getPodModeCapability(pod.platform, podMode);
	const canUsePodMode = modeCapability.enabled;
	const canStartOrSchedule = hasProfile && hasActiveUrls && canUsePodMode;

	const formatScheduleTime = (isoString: string) => {
		if (!isoString) return 'Not set';

		const localTime = formatInTimeZone(
			new Date(isoString),
			Intl.DateTimeFormat().resolvedOptions().timeZone,
			'MM/dd/yyyy, h:mm a'
		);

		const timeAgo = formatDistanceToNow(new Date(isoString), {
			addSuffix: true,
		});

		return `${localTime} • ${timeAgo}`;
	};

	return (
		<Card className='border-4 mb-11'>
			<CardHeader className='space-y-8'>
				<Button
					variant='outline'
					size='lg'
					onClick={() => {
						setSelectedPod(null);
						navigate('/admin/pods', { replace: true });
					}}
					className='hover:bg-secondary text-2xl font-bold px-8 py-6'
				>
					← BACK TO ALL PODS
				</Button>

				<div className='space-y-4'>
					{/* Title and Status */}
					<div className='space-y-2'>
						<CardTitle className='flex items-center gap-2 text-3xl'>
							<span className='text-pink-500 font-bold'>
								POD:
							</span>
							{pod.name}
						</CardTitle>
						<div className='flex items-center justify-between'>
							<div className='flex items-center gap-4'>
								<span
									className={cn(
										'px-4 py-2 rounded-full text-base font-bold',
										pod.isActive
											? 'bg-gradient-to-b from-emerald-400 to-green-600 text-white shadow-lg'
											: 'bg-gradient-to-b from-orange-400 to-red-600 text-white shadow-lg'
									)}
								>
									{pod.isActive ? '🟢 ACTIVE' : '🔴 INACTIVE'}
								</span>
								<span className='text-xl text-muted-foreground'>
									{pod.platform.toUpperCase()} POD
								</span>
							</div>
							<span
								className={cn(
									'px-8 py-4 rounded-full text-2xl font-bold text-white shadow-lg',
									pod.mode === 'broadcast'
										? 'bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700'
										: 'bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800',
									'transition-all duration-300'
								)}
								style={{
									animation: 'glow 2s ease-in-out infinite',
									boxShadow: (() => {
										if (!podSchedules?.length)
											return '0 0 50px rgba(239, 68, 68, 0.8)'; // Red - no schedules
										if (
											podSchedules.some(
												(s) => s.status === 'running'
											)
										)
											return '0 0 50px rgba(34, 197, 94, 0.8)'; // Green - running
										if (
											podSchedules.some(
												(s) => s.status === 'active'
											)
										)
											return '0 0 50px rgba(147, 51, 234, 0.8)'; // Purple - active
										return '0 0 50px rgba(234, 179, 8, 0.8)'; // Yellow - all paused
									})(),
								}}
							>
								{(pod.mode || 'swarm').toUpperCase()} MODE
							</span>
						</div>
					</div>
				</div>

				<div className='mt-8'>
					<ScheduleStatusBadge
						status={pod.isActive ? 'active' : 'inactive'}
						lastStatus={{
							status:
								pod.lastStatus?.status ||
								(pod.isActive ? 'READY' : 'INACTIVE'),
							message:
								pod.lastStatus?.message ||
								(!podSchedules || !podSchedules.length
									? 'No schedules set for this pod'
									: pod.isActive
										? 'Schedule is active and waiting for next run'
										: 'Schedule is not active'),
						}}
					/>
					{podSchedules && podSchedules.length > 0 && (
						<div className='space-y-1 mt-4'>
							<div className='grid grid-cols-2 gap-4'>
								<div>
									<div className='text-sm text-gray-900 dark:text-white font-bold mb-1'>
										Next run:
									</div>
									<div className='text-sm'>
										{podSchedules[0].nextRun ? (
											<>
												{new Date(
													podSchedules[0].nextRun
												).toLocaleString('en-US', {
													timeZone:
														podSchedules[0].schedule
															.timezone,
													hour: 'numeric',
													minute: '2-digit',
													year: 'numeric',
													month: '2-digit',
													day: '2-digit',
													hour12: true,
													timeZoneName: 'short',
												})}
												{' • '}
												<span className='text-muted-foreground/70'>
													(
													{formatDistanceToNow(
														new Date(
															podSchedules[0].nextRun
														)
													)}
													)
												</span>
											</>
										) : (
											<span className='text-muted-foreground/50'>
												Never
											</span>
										)}
									</div>
								</div>
								<div className='text-left pl-8'>
									<div className='text-sm text-gray-900 dark:text-white font-bold mb-1'>
										Last run:
									</div>
									<div className='text-sm'>
										{podSchedules[0].lastRun ? (
											<>
												{new Date(
													podSchedules[0].lastRun
												).toLocaleString('en-US', {
													timeZone:
														podSchedules[0].schedule
															.timezone,
													hour: 'numeric',
													minute: '2-digit',
													year: 'numeric',
													month: '2-digit',
													day: '2-digit',
													hour12: true,
													timeZoneName: 'short',
												})}
												{' • '}
												<span className='text-muted-foreground/70'>
													(
													{formatDistanceToNow(
														new Date(
															podSchedules[0].lastRun
														)
													)}{' '}
													ago)
												</span>
											</>
										) : (
											<span className='text-muted-foreground/50'>
												Never
											</span>
										)}
									</div>
								</div>
							</div>
							<div className='h-4 mb-8'></div>
							{podSchedules[0].lastRunStatus && (
								<div>
									<div className='p-4 rounded-lg bg-gray-100/50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800'>
										<div className='flex items-start gap-2'>
											<span className='mt-1'>📝</span>
											<div>
												<span className='text-lg font-semibold text-gray-900 dark:text-white'>
													Last Run:{' '}
												</span>
												<span className='text-lg text-orange-600 dark:text-orange-400'>
													{podSchedules[0]
														.lastRunStatus
														.message ||
														'No run message'}
												</span>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</CardHeader>

			<CardContent className='space-y-12 overflow-x-hidden'>
				<div className='py-8'>
					<div className='flex justify-between items-center gap-4'>
						<div className='flex gap-2'>
							<Button
								size='lg'
								onClick={() => handleToggleActive(pod._id)}
								disabled={
									podButtonDisabled[pod._id] ||
									runningPods[pod._id] ||
									!canUsePodMode
								}
								className={cn(
									'font-semibold shadow-lg transition-all duration-200 px-6',
									pod.isActive
										? 'bg-gradient-to-b from-orange-400 to-red-600 hover:from-orange-500 hover:to-red-700 text-white'
										: 'bg-gradient-to-b from-emerald-400 to-green-600 hover:from-emerald-500 hover:to-green-700 text-white'
								)}
							>
								<span className='whitespace-nowrap'>
									{pod.isActive
										? '🚫 Deactivate Pod'
										: '✅ Activate Pod'}
								</span>
							</Button>
							<Button
								size='lg'
								onClick={() => setPodToDelete(pod._id)}
								disabled={isPodRunning(pod.lastStatus?.status)}
								className='bg-gradient-to-b from-red-400 to-pink-600 hover:from-red-500 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
							>
								<span className='whitespace-nowrap'>
									🗑️ Delete Pod
								</span>
							</Button>
							<Button
								size='lg'
								onClick={() =>
									setPodToRename({
										id: pod._id,
										name: pod.name,
									})
								}
								disabled={isPodRunning(pod.lastStatus?.status)}
								className='bg-gradient-to-b from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
							>
								<span className='whitespace-nowrap'>
									✏️ Rename Pod
								</span>
							</Button>
							<Button
								size='lg'
								onClick={() => setShowResetConfirm(true)}
								className='bg-gradient-to-b from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
							>
								<span className='whitespace-nowrap'>
									🔄 Reset Status
								</span>
							</Button>
						</div>
						<div className='flex gap-2'>
							{/* Commenting out Start Pod button for now
							<div className='relative group'>
								<Button
									size='lg'
									onClick={async () => {
										try {
											if (
												!pod.members ||
												pod.members.length === 0
											) {
												toast({
													title: '⚠️ Cannot Start Pod',
													description:
														'Please add at least one member to the pod before starting.',
													variant: 'destructive',
													duration: 3000,
												});
												return;
											}

											const urls = await fetchPodUrls(
												pod._id
											);
											const activeUrls = urls.filter(
												(url) => url.isActive
											);

											if (activeUrls.length === 0) {
												toast({
													title: 'No Active URLs',
													description:
														'Please add and activate some URLs before starting the pod.',
													variant: 'destructive',
													duration: 3000,
												});
												return;
											}

											setPodToStart({
												podId: pod._id,
												urls: activeUrls,
											});
										} catch (error) {
											console.error(
												'Failed to fetch pod URLs:',
												error
											);
											toast({
												title: 'Error',
												description:
													'Failed to start pod: ' +
													(error instanceof Error
														? error.message
														: 'Unknown error'),
												variant: 'destructive',
												duration: 3000,
											});
										}
									}}
									className='bg-gradient-to-b from-emerald-400 to-teal-600 hover:from-emerald-500 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
									disabled={
										!canStartOrSchedule ||
										isPodRunning(pod.lastStatus?.status)
									}
								>
									<span className='whitespace-nowrap'>
										✨ Start Pod
									</span>
								</Button>
								{!canStartOrSchedule && (
									<div className='absolute -top-12 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none'>
										Add a social profile and URLs before
										starting the pod
									</div>
								)}
							</div>
							*/}
							<div className='relative group'>
								<Button
									size='lg'
									onClick={() =>
										navigate('/admin/schedules', {
											state: {
												selectedPod: pod,
												fromPodView: true,
											},
										})
									}
									className='bg-gradient-to-b from-violet-400 to-purple-600 hover:from-violet-500 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
									disabled={!canStartOrSchedule}
								>
									<span className='whitespace-nowrap'>
										🕒 Schedule
									</span>
								</Button>
								{!canStartOrSchedule && (
									<div className='absolute -top-12 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none'>
										{!canUsePodMode
											? `${getPodPlatformLabel(pod.platform)} ${podMode} scheduling is disabled`
											: 'Add a social profile and URLs before scheduling the pod'}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className='h-px bg-gray-200 dark:bg-gray-800 my-16' />
				{!canUsePodMode && (
					<div className='rounded-md border border-orange-300 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
						<div className='font-semibold'>
							{getPodPlatformLabel(pod.platform)} {podMode} pod
							mode disabled
						</div>
						<div className='mt-1'>{modeCapability.reason}</div>
					</div>
				)}

				<div className='my-12'>
					<div className='flex items-center justify-between mb-4'>
						<div className='space-y-1'>
							<h3 className='text-3xl font-bold'>
								{pod.mode === 'broadcast'
									? 'Pod Social Profile'
									: 'Pod Social Profiles'}
							</h3>
							<p className='text-sm text-gray-500'>
								{pod.mode === 'broadcast'
									? 'Select the social profile that will be used for engagement'
									: 'Add members to engage with content as a group'}
							</p>
						</div>
						{pod.mode === 'broadcast' && pod.members.length > 0 && (
							<Button
								variant='destructive'
								size='lg'
								onClick={() => {
									setMemberToRemove({
										podId: pod._id,
										email: pod.members[0].email,
									});
								}}
								className='bg-gradient-to-b from-red-400 to-pink-600 hover:from-red-500 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
							>
								<span className='inline-flex items-center gap-2'>
									<span className='text-xl'>🗑️</span>
									<span>Remove Profile</span>
								</span>
							</Button>
						)}
					</div>
					{pod.mode === 'broadcast' && pod.members.length > 0 ? (
						<ProfileCard profile={pod.members[0]} pod={pod} />
					) : pod.mode === 'swarm' ? (
						<AddProfileSection
							pod={pod}
							typedProfiles={typedProfiles || []}
							newMember={newMember}
							setNewMember={setNewMember}
							handleAddMember={async (podId, member, triggerConfettiEffect) => {
								setIsAddingMember(true);
								try {
									await handleAddMember(podId, member, triggerConfettiEffect);
								} finally {
									setIsAddingMember(false);
								}
							}}
							isAddingMember={isAddingMember}
						/>
					) : pod.mode === 'broadcast' ? (
						<AddProfileSection
							pod={pod}
							typedProfiles={typedProfiles || []}
							newMember={newMember}
							setNewMember={setNewMember}
							handleAddMember={async (podId, member, triggerConfettiEffect) => {
								setIsAddingMember(true);
								try {
									await handleAddMember(podId, member, triggerConfettiEffect);
								} finally {
									setIsAddingMember(false);
								}
							}}
							isAddingMember={isAddingMember}
						/>
					) : null}

					{pod.mode === 'swarm' && (
						<div className='mt-8 overflow-x-hidden'>
							<div className='flex items-center gap-2'>
								<motion.button
									onClick={() =>
										setExpandedGroups((prev) => ({
											...prev,
											main: !prev.main,
										}))
									}
									className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
									animate={{
										rotate: expandedGroups.main ? 180 : 0,
									}}
									transition={{ duration: 0.2 }}
								>
									{expandedGroups.main ? '🔽' : '▶️'}
								</motion.button>
								<h3 className='text-3xl font-bold'>
									Connected Tribes
								</h3>
							</div>

							<AnimatePresence>
								{expandedGroups.main && (
									<motion.div
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										transition={{ duration: 0.2 }}
									>
										<Card className='mt-4'>
											<CardContent className='pt-6'>
												<div className='space-y-4'>
													<div className='flex flex-col gap-2'>
														<Label>
															Select Tribe
														</Label>
														<SearchableSelect
															options={
																profileGroups?.map((group) => {
																	// Check if group has any profiles that can be added
																	const availableCount = group.profileIds.filter((id) => {
																		const profile = typedProfiles?.find((p) => p._id === id);
																		if (!profile) return false;
																		return !pod.members.some(
																			(member) =>
																				member.email === profile.email ||
																				member.uploadPostProfileKey === profile.profileKey
																		);
																	}).length;
																	
																	return {
																		value: group._id,
																		label: group.name,
																		data: group,
																		render: () => (
																			<div className='flex flex-col py-1'>
																				<div className='font-medium'>{group.name}</div>
																				<div className='text-sm text-gray-500'>
																					{availableCount} profile{availableCount !== 1 ? 's' : ''} available
																					{group.description && ` - ${group.description}`}
																				</div>
																			</div>
																		),
																	};
																}) || []
															}
															value={selectedGroupId || ''}
															onChange={(value) => {
																setSelectedGroupId(value ? (value as Id<'profileGroups'>) : null);
															}}
															placeholder='Select a tribe...'
															maintainFocus={true}
														/>

														<Button
															onClick={async () => {
																if (
																	!selectedGroupId
																)
																	return;
																const selectedGroup =
																	profileGroups?.find(
																		(g) =>
																			g._id ===
																			selectedGroupId
																	);
																if (
																	!selectedGroup
																)
																	return;

																const groupMembers =
																	selectedGroup.profileIds
																		.map(
																			(
																				id
																			) =>
																				typedProfiles?.find(
																					(
																						p
																					) =>
																						p._id ===
																						id
																				)
																		)
																		.filter(
																			Boolean
																		)
																		.filter(
																			(
																				profile
																			) =>
																				!pod.members.some(
																					(
																						member
																					) =>
																						member.email ===
																							profile.email ||
																						member.uploadPostProfileKey ===
																							profile.profileKey
																				)
																		)
																		.map(
																			(
																				profile
																			) => ({
																				email: profile.email,
																				socialUsername:
																					profile.profileTitle,
																				uploadPostProfileKey:
																					profile.profileKey,
																			})
																		);

																if (
																	groupMembers.length ===
																	0
																) {
																	toast({
																		title: 'No New Members',
																		description:
																			'All profiles from this group are already in the pod.',
																		variant:
																			'default',
																		duration: 3000,
																	});
																	return;
																}

																setAddProgress({
																	current: 0,
																	total: groupMembers.length,
																	currentProfile:
																		'',
																});

																for (
																	let i = 0;
																	i <
																	groupMembers.length;
																	i++
																) {
																	setAddProgress(
																		(
																			prev
																		) => ({
																			...prev!,
																			current:
																				i,
																			currentProfile:
																				groupMembers[
																					i
																				]
																					.email,
																		})
																	);
																	await handleAddMember(
																		pod._id,
																		groupMembers[
																			i
																		],
																		false
																	);
																}

																triggerConfetti();
																setAddProgress(
																	null
																);
																setSelectedGroupId(
																	null
																);
															}}
															disabled={
																!selectedGroupId
															}
															className='w-full bg-gradient-to-b from-purple-300 via-purple-500 to-purple-800 text-white hover:opacity-90'
														>
															➕ Add Tribe
														</Button>
													</div>

													{profileGroups
														?.filter((group) =>
															pod.members.some(
																(member) =>
																	group.profileIds.some(
																		(
																			profileId
																		) =>
																			typedProfiles?.find(
																				(
																					p
																				) =>
																					p._id ===
																						profileId &&
																					p.email ===
																						member.email
																			)
																	)
															)
														)
														.map((group) => {
															const groupMembers =
																pod.members.filter(
																	(member) =>
																		group.profileIds.some(
																			(
																				profileId
																			) =>
																				typedProfiles?.find(
																					(
																						p
																					) =>
																						p._id ===
																						profileId
																				)
																					?.email ===
																				member.email
																		)
																);

															return (
																<Card
																	key={
																		group._id
																	}
																	className='mt-4'
																>
																	<CardContent className='pt-4'>
																		<div className='flex items-center justify-between mb-4'>
																			<div className='flex items-center gap-2'>
																				<motion.button
																					onClick={() =>
																						setExpandedGroups(
																							(
																								prev
																							) => ({
																								...prev,
																								[group._id]:
																									!prev[
																										group
																											._id
																									],
																							})
																						)
																					}
																					className='w-6 h-6 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
																					animate={{
																						rotate: expandedGroups[
																							group
																								._id
																						]
																							? 180
																							: 0,
																					}}
																					transition={{
																						duration: 0.2,
																					}}
																				>
																					{expandedGroups[
																						group
																							._id
																					]
																						? '🔽'
																						: '▶️'}
																				</motion.button>
																				<h4 className='text-lg font-semibold'>
																					{
																						group.name
																					}
																				</h4>
																				<Badge variant='secondary'>
																					{
																						groupMembers.length
																					}{' '}
																					members
																				</Badge>
																			</div>
																			<Button
																				variant='destructive'
																				size='sm'
																				onClick={() =>
																					setGroupToRemove(
																						{
																							groupId:
																								group._id,
																							podId: pod._id,
																							emails: groupMembers.map(
																								(
																									member
																								) =>
																									member.email
																							),
																						}
																					)
																				}
																				className='whitespace-nowrap'
																			>
																				🗑️ Remove Tribe
																			</Button>
																		</div>
																		<AnimatePresence>
																			{expandedGroups[
																				group
																					._id
																			] && (
																				<motion.div
																					initial={{
																						opacity: 0,
																						height: 0,
																					}}
																					animate={{
																						opacity: 1,
																						height: 'auto',
																					}}
																					exit={{
																						opacity: 0,
																						height: 0,
																					}}
																					transition={{
																						duration: 0.2,
																					}}
																					className='space-y-2'
																				>
																					{groupMembers.map(
																						(
																							member,
																							idx
																						) => (
																							<motion.div
																								key={
																									member.email
																								}
																								initial={{
																									opacity: 0,
																									y: -10,
																								}}
																								animate={{
																									opacity: 1,
																									y: 0,
																									transition:
																										{
																											delay:
																												idx *
																												0.05,
																										},
																								}}
																								exit={{
																									opacity: 0,
																									y: -10,
																									transition:
																										{
																											delay:
																												(groupMembers.length -
																													idx -
																													1) *
																												0.05,
																										},
																								}}
																								className='flex items-center justify-between p-2 bg-secondary rounded-md'
																							>
																								<span>
																									{
																										member.email
																									}
																								</span>
																								<Button
																									variant='destructive'
																									size='sm'
																									onClick={() =>
																										setMemberToRemove(
																											{
																												email: member.email,
																												podId: pod._id,
																											}
																										)
																									}
																									className='h-7 px-2'
																								>
																									✕
																								</Button>
																							</motion.div>
																						)
																					)}
																				</motion.div>
																			)}
																		</AnimatePresence>
																	</CardContent>
																</Card>
															);
														})}
												</div>
											</CardContent>
										</Card>
									</motion.div>
								)}
							</AnimatePresence>
							
							{/* Show individual members not in tribes */}
							{(() => {
								const individualMembers = pod.members.filter(
									(member) =>
										!profileGroups?.some((group) =>
											group.profileIds.some((profileId) =>
												typedProfiles?.find(
													(p) => p._id === profileId && p.email === member.email
												)
											)
										)
								);
								
								return individualMembers.length > 0 ? (
									<div className='mt-8'>
										<h3 className='text-2xl font-bold mb-4'>Individual Profiles</h3>
										<div className='space-y-2'>
											{individualMembers.map((member) => (
												<div key={member.email} className='relative overflow-hidden'>
													<div className='absolute right-12 top-[50%] -translate-y-[50%] z-10'>
														<Button
															variant='destructive'
															size='sm'
															onClick={() =>
																setMemberToRemove({
																	email: member.email,
																	podId: pod._id,
																})
															}
															className='h-8 px-3'
														>
															Remove Profile
														</Button>
													</div>
													<ProfileCard profile={member} pod={pod} />
												</div>
											))}
										</div>
									</div>
								) : null;
							})()}
						</div>
					)}

					<div className='mt-8'>
						<URLsSection
							pod={pod}
							urls={urls}
							handleToggleUrlActive={handleToggleUrlActive}
							setUrlToArchive={setUrlToArchive}
							urlButtonDisabled={urlButtonDisabled}
							setShowAddUrlDialog={setShowAddUrlDialog}
							hasProfile={pod.members.length > 0}
							expandedGroups={expandedGroups}
							setExpandedGroups={setExpandedGroups}
						/>
					</div>
				</div>

				<div className='h-px bg-gray-200 dark:bg-gray-800 my-16' />

				{pod.mode === 'broadcast' && (
					<div className='my-12'>
						<div className='flex items-center justify-between mb-8'>
							<div className='space-y-1'>
								<h3 className='text-3xl font-bold'>
									Pod Brand Voice
								</h3>
								<p className='text-sm text-gray-500'>
									Selected voice will be used for generating
									engagement comments and responses
								</p>
							</div>
						<div className='flex gap-2'>
							{documents && documents.length > 0 && (
								<Button
									variant='outline'
									size='sm'
									asChild
									className='bg-purple-100 hover:bg-purple-200 text-purple-700'
								>
									<Link to='/admin/brand-voice'>
										✏️ Edit My Brand Voice
									</Link>
								</Button>
							)}
						</div>
					</div>

					<Card>
						<CardHeader>
							<CardTitle className='text-pink-500 text-3xl font-bold'>
								Select Voice Style
							</CardTitle>
						</CardHeader>
						<CardContent>
							<RadioGroup
								value={selectedBrandVoice}
								onValueChange={async (value) => {
									// Get voice details for confirmation
									let voiceDetails;
									if (value === 'user' && userBrandVoice) {
										voiceDetails = {
											value,
											name: userBrandVoice.name,
											description:
												userBrandVoice.description,
											emoji: userBrandVoice.emoji,
										};
									} else {
										const voice = BRAND_VOICES.find(
											(v) => v.id === value
										);
										if (voice) {
											voiceDetails = {
												value,
												name: voice.name,
												description: voice.description,
												emoji: voice.emoji,
											};
										}
									}

									if (voiceDetails) {
										setBrandVoiceToConfirm(voiceDetails);
									}
								}}
								className='space-y-4'
							>
								{userBrandVoice && (
									<Label
										htmlFor='user'
										className={cn(
											brandVoiceStyles.base,
											selectedBrandVoice === 'user'
												? brandVoiceStyles.selected
												: brandVoiceStyles.unselected
										)}
									>
										<RadioGroupItem
											value='user'
											id='user'
											className={brandVoiceStyles.radio}
										>
											<span
												className={
													brandVoiceStyles.radioIcon
												}
											>
												✨
											</span>
										</RadioGroupItem>
										<div
											className={brandVoiceStyles.content}
										>
											<div className='flex items-center gap-2 text-lg font-semibold'>
												<span className='text-2xl'>
													{userBrandVoice.emoji}
												</span>
												<span>
													{userBrandVoice.name}
												</span>
											</div>
											<div className='flex items-center justify-between'>
												<div className='flex-1 min-w-0 pr-4'>
													<p className='text-sm text-muted-foreground'>
														{
															userBrandVoice.description
														}
													</p>
												</div>
												<Button
													variant='outline'
													size='sm'
													onClick={(e) => {
														e.preventDefault();
														const analysis = userProfile?.writingAnalysis || 
															(documents && documents.length > 0 ? 'Brand voice based on your documents' : 'No analysis available');
														setCurrentAnalysis(
															analysis
														);
														setViewingBrandVoice({
															id: 'user',
															name: userBrandVoice.name,
															emoji: userBrandVoice.emoji,
														});
														setShowAnalysisDialog(
															true
														);
													}}
													className='relative group bg-gradient-to-br from-purple-500/80 via-pink-500/80 to-purple-500/80 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap ml-4 h-[70px] px-6'
												>
													<span className='flex flex-col items-center gap-1.5'>
														<span className='flex items-center gap-1'>
															✨ View Brand Voice
														</span>
													</span>
												</Button>
											</div>
										</div>
									</Label>
								)}

								{BRAND_VOICES.map((voice) => (
									<Label
										key={voice.id}
										htmlFor={voice.id}
										className={cn(
											brandVoiceStyles.base,
											selectedBrandVoice === voice.id
												? brandVoiceStyles.selected
												: brandVoiceStyles.unselected
										)}
									>
										<RadioGroupItem
											value={voice.id}
											id={voice.id}
											className={brandVoiceStyles.radio}
											disabled={isUpdatingBrandVoice}
										>
											<span
												className={
													brandVoiceStyles.radioIcon
												}
											>
												{isUpdatingBrandVoice
													? '⏳'
													: '✨'}
											</span>
										</RadioGroupItem>
										<div
											className={brandVoiceStyles.content}
										>
											<div className='flex items-center gap-2 text-lg font-semibold'>
												<span className='text-2xl'>
													{voice.emoji}
												</span>
												<span>{voice.name}</span>
											</div>
											<div className='flex items-center justify-between'>
												<div className='flex-1 min-w-0 pr-4'>
													<p className='text-sm text-muted-foreground'>
														{voice.description}
													</p>
													<p className='text-xs text-gray-500'>
														Style: {voice.style}
													</p>
												</div>
												<Button
													variant='outline'
													size='sm'
													onClick={(e) => {
														e.preventDefault();
														let analysis = '';
														if (
															voice.id ===
															'prometheus'
														) {
															analysis =
																'🔥 Prometheus is our default AI voice, designed to create engaging social media content with a mix of professionalism and personality.';
														} else if (
															voice.id === 'brent'
														) {
															analysis =
																brentProfile?.writingAnalysis ||
																'No analysis available';
														} else if (
															voice.id ===
															'reverend'
														) {
															analysis =
																reverendProfile?.writingAnalysis ||
																'No analysis available';
														}
														setCurrentAnalysis(
															analysis
														);
														setViewingBrandVoice({
															id: voice.id,
															name: voice.name,
															emoji: voice.emoji,
														});
														setShowAnalysisDialog(
															true
														);
													}}
													className='relative group bg-gradient-to-br from-purple-500/80 via-pink-500/80 to-purple-500/80 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap ml-4 h-[70px] px-6'
												>
													<span className='flex flex-col items-center gap-1.5'>
														<span className='flex items-center gap-1'>
															✨ View Brand Voice
														</span>
													</span>
												</Button>
											</div>
										</div>
									</Label>
								))}
							</RadioGroup>
						</CardContent>
					</Card>
				</div>
				)}

				<ConfirmDialog
					isOpen={podToDelete !== null}
					onClose={() => setPodToDelete(null)}
					title='Delete Pod'
					description='Are you sure you want to delete this pod? This action cannot be undone.'
					onConfirm={async () => {
						if (podToDelete) {
							await handleDeletePod(podToDelete);
							setPodToDelete(null);
							onClose();
						}
					}}
				/>
				<Dialog
					open={podToRename !== null}
					onOpenChange={(open) => !open && setPodToRename(null)}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Rename Pod</DialogTitle>
						</DialogHeader>
						<Input
							placeholder='New pod name'
							value={podToRename?.name ?? ''}
							onChange={(e) =>
								setPodToRename((prev) =>
									prev
										? { ...prev, name: e.target.value }
										: null
								)
							}
						/>
						<DialogFooter>
							<Button
								variant='outline'
								onClick={() => setPodToRename(null)}
							>
								Cancel
							</Button>
							<Button
								onClick={async () => {
									if (podToRename) {
										await handleRenamePod(
											podToRename.id,
											podToRename.name
										);
										setPodToRename(null);
									}
								}}
								disabled={!podToRename?.name.trim()}
							>
								Save
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
				<ProgressDialog
					isOpen={removeProgress !== null}
					title='Removing Profiles'
					description='Please wait while profiles are being removed from the pod...'
					progress={
						((removeProgress?.current || 0) /
							(removeProgress?.total || 1)) *
						100
					}
					current={removeProgress?.current || 0}
					total={removeProgress?.total || 0}
				/>
				<ProgressDialog
					isOpen={addProgress !== null}
					title='Adding Profiles'
					description='Please wait while profiles are being added to the pod...'
					progress={
						((addProgress?.current || 0) /
							(addProgress?.total || 1)) *
						100
					}
					current={addProgress?.current || 0}
					total={addProgress?.total || 0}
				/>
				<ConfirmDialog
					isOpen={memberToRemove !== null}
					onClose={() => setMemberToRemove(null)}
					title='Remove Profile from Pod'
					description={
						<div className='space-y-4'>
							<div className='text-lg text-red-500 font-semibold'>
								⚠️ Warning: This action will:
							</div>
							<ul className='list-disc pl-6 space-y-2 text-orange-500'>
								<li>Remove the profile from this pod</li>
								{pod.mode === 'broadcast' && (
									<li>
										Unlink all associated URLs from this pod
										(URLs will be preserved but deactivated)
									</li>
								)}
							</ul>
							<p className='text-base text-gray-400 mt-4'>
								Are you sure you want to proceed?
							</p>
						</div>
					}
					onConfirm={async () => {
						if (memberToRemove) {
							await handleRemoveMember(
								memberToRemove.podId,
								memberToRemove.email
							);
							setMemberToRemove(null);
							setNewMember({
								email: '',
								socialUsername: '',
								uploadPostProfileKey: '',
							});
							toast({
								title: '✨ Profile Removed',
								description:
									'Profile has been removed from the pod successfully.',
								duration: 3000,
							});
						}
					}}
					confirmText='Remove Profile'
					cancelText='Cancel'
				/>
				<ConfirmDialog
					isOpen={groupToRemove !== null}
					onClose={() => setGroupToRemove(null)}
					title='Remove Group from Pod'
					description={
						<div className='text-lg text-red-500 font-semibold mt-2'>
							⚠️ Warning: This action cannot be undone! ⚠️
							<p className='text-base text-orange-500 mt-2'>
								Are you sure you want to remove this entire
								group ({groupToRemove?.emails.length} members)
								from the pod?
							</p>
						</div>
					}
					onConfirm={async () => {
						if (groupToRemove) {
							setRemoveProgress({
								current: 0,
								total: groupToRemove.emails.length,
								currentProfile: '',
							});

							await handleBulkRemoveMembers(
								groupToRemove.podId,
								groupToRemove.emails,
								(current, email) => {
									setRemoveProgress((prev) => ({
										current,
										total: groupToRemove.emails.length,
										currentProfile: email,
									}));
								}
							);

							setRemoveProgress(null);
							setGroupToRemove(null);
						}
					}}
				/>
				<ConfirmDialog
					isOpen={urlToArchive !== null}
					onClose={() => setUrlToArchive(null)}
					onConfirm={async () => {
						if (urlToArchive) {
							await handleArchiveUrl(urlToArchive);
							setUrlToArchive(null);
						}
					}}
					title='Archive URL'
					description={
						<div className='text-lg mt-2'>
							Are you sure you want to archive this URL?
							<br />
							You can view archived URLs in the history page.
						</div>
					}
					confirmText='Archive'
					cancelText='Cancel'
				/>
				<AddUrlDialog
					pod={pod}
					isOpen={showAddUrlDialog}
					onClose={() => setShowAddUrlDialog(false)}
				/>
				<ConfirmDialog
					isOpen={showResetConfirm}
					onClose={() => setShowResetConfirm(false)}
					title='Reset Pod Status'
					description={
						<div className='text-lg mt-2'>
							Are you sure you want to reset this pod's status?
							<br />
							This will clear the current status and message.
						</div>
					}
					onConfirm={async () => {
						try {
							await updateStatus({
								podId: pod._id,
								status: '',
								message: '',
								timestamp: new Date().toISOString(),
							});
							setRunningPods((prev) => ({
								...prev,
								[pod._id]: false,
							}));
							toast({
								title: '🔄 Status Reset',
								description:
									'Pod status has been reset successfully.',
								duration: 3000,
							});
							setShowResetConfirm(false);
						} catch (error) {
							toast({
								title: 'Error',
								description:
									error instanceof Error
										? error.message
										: 'Failed to reset pod status',
								variant: 'destructive',
								duration: 3000,
							});
						}
					}}
					confirmText='Reset'
					cancelText='Cancel'
				/>
				<Dialog
					open={showAnalysisDialog}
					onOpenChange={(open) => {
						if (!open) {
							setViewingBrandVoice(null);
						}
						setShowAnalysisDialog(open);
					}}
				>
					<DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden'>
						<div className='flex flex-col h-[calc(80vh-2rem)]'>
							<DialogHeader className='flex-none bg-background py-4 border-b'>
								<DialogTitle className='text-2xl font-bold flex items-center gap-2 pr-8'>
									{viewingBrandVoice?.emoji}{' '}
									{viewingBrandVoice?.name} - Brand Voice
									Analysis
								</DialogTitle>
							</DialogHeader>

							<div className='flex-1 overflow-y-auto'>
								<div className='p-6 bg-gray-50 dark:bg-gray-900 rounded-lg'>
									<div className='prose dark:prose-invert max-w-none'>
										<ReactMarkdown
											components={{
												p: ({ children }) => (
													<p className='mb-4 text-gray-600 dark:text-gray-300'>
														{children}
													</p>
												),
												strong: ({ children }) => (
													<strong className='font-bold text-gray-900 dark:text-white'>
														{children}
													</strong>
												),
												em: ({ children }) => (
													<em className='text-pink-600 dark:text-pink-400 not-italic font-medium'>
														{children}
													</em>
												),
											}}
										>
											{currentAnalysis || ''}
										</ReactMarkdown>
									</div>
								</div>
							</div>
						</div>
					</DialogContent>
				</Dialog>
				<Dialog
					open={!!brandVoiceToConfirm}
					onOpenChange={() => setBrandVoiceToConfirm(null)}
				>
					<DialogContent className='max-w-md'>
						<DialogHeader>
							<DialogTitle className='text-2xl flex items-center gap-2'>
								{brandVoiceToConfirm?.emoji} Change Brand Voice?
							</DialogTitle>
						</DialogHeader>

						<div className='py-4'>
							<h3 className='text-lg font-semibold mb-2 flex items-center gap-2'>
								{brandVoiceToConfirm?.emoji}{' '}
								{brandVoiceToConfirm?.name}
							</h3>
							<p className='text-gray-600 dark:text-gray-300 mb-4'>
								{brandVoiceToConfirm?.description}
							</p>

							{brandVoiceToConfirm?.value === 'reverend' && (
								<div className='p-4 bg-orange-100 dark:bg-orange-900/20 rounded-lg border-2 border-orange-300 dark:border-orange-700'>
									<div className='font-semibold text-orange-800 dark:text-orange-200 flex items-center gap-2'>
										⚠️ Spicy Content Warning 🌶️
									</div>
									<div className='text-orange-700 dark:text-orange-300 mt-2'>
										Heads up! The Reverend doesn't hold back
										and might drop some 🤬 curse words in
										the comments. Perfect for edgy content,
										but maybe not for your mom's cooking
										blog! 😅
									</div>
								</div>
							)}
						</div>

						<DialogFooter className='flex gap-2'>
							<Button
								variant='outline'
								onClick={() => setBrandVoiceToConfirm(null)}
							>
								Cancel
							</Button>
							<Button
								className={cn(
									'text-white',
									brandVoiceToConfirm?.value === 'reverend'
										? 'bg-orange-500 hover:bg-orange-600'
										: 'bg-pink-500 hover:bg-pink-600'
								)}
								onClick={async () => {
									if (!brandVoiceToConfirm) return;

									try {
										setIsUpdatingBrandVoice(true);
										const value = brandVoiceToConfirm.value;

										let profileId:
											| Id<'profiles'>
											| undefined;
										switch (value) {
											case 'user':
												profileId = userProfile?._id;
												break;
											case 'brent':
												profileId = brentProfile?._id;
												break;
											case 'reverend':
												profileId =
													reverendProfile?._id;
												break;
											case 'prometheus':
												profileId = undefined;
												break;
										}

										await updateBrandVoice({
											podId: pod._id,
											brandVoiceId: value,
											brandVoiceProfileId: profileId,
										});

										setSelectedBrandVoice(value);
										setBrandVoiceToConfirm(null);

										toast({
											title: '🎭 Brand Voice Updated',
											description:
												value === 'prometheus' ? (
													<>
														Using{' '}
														<strong>
															🔥 Prometheus
														</strong>
														, the default AI voice
														for engagement
													</>
												) : (
													<>
														Pod will now use{' '}
														<strong>
															{
																brandVoiceToConfirm.emoji
															}{' '}
															{
																brandVoiceToConfirm.name
															}
														</strong>{' '}
														for generating comments
													</>
												),
											duration: 3000,
										});
									} catch (error) {
										console.error('Detailed error:', error);
										toast({
											title: 'Error',
											description:
												'Failed to update brand voice',
											variant: 'destructive',
											duration: 3000,
										});
										setSelectedBrandVoice(
											pod.brandVoice?.id || 'prometheus'
										);
									} finally {
										setIsUpdatingBrandVoice(false);
									}
								}}
							>
								{brandVoiceToConfirm?.value === 'reverend'
									? "🎭 Let's Get Spicy!"
									: '✨ Confirm Change'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
};
